<template>
  <div id="app">
    <!-- Hidden SVG filter bank — provides the hand-drawn wobble effect -->
    <svg xmlns="http://www.w3.org/2000/svg" width="0" height="0" style="position:absolute;">
      <defs>
        <filter id="sketchy-border" x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence type="fractalNoise" baseFrequency="0.065" numOctaves="3" seed="7" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="2.2" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <!-- Tuned for horizontal separator lines: Frequency≈75%, Wiggle≈30% -->
        <filter id="hand-drawn-line" x="-2%" y="-200%" width="104%" height="500%">
          <feTurbulence type="fractalNoise" baseFrequency="0.075 0.02" numOctaves="2" seed="3" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <!-- Tuned for button box borders: Frequency 32%, Wiggle 30%, Smooth 50% -->
        <filter id="button-stroke" x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence type="fractalNoise" baseFrequency="0.032" numOctaves="2" seed="5" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </defs>
    </svg>
    <component
      :is="$store.state.screen"
    />
    <div v-for="font in $store.state.fonts" :key="font" class="lazy-font-loader" :style="{ fontFamily: font }">
      i contain text so the font will load
    </div>
  </div>
</template>

<script>

import SplashScreen from './components/SplashScreen.vue';
import ChooseFoldScreen from './components/ChooseFoldScreen.vue';
import PersonalizeScreen from './components/PersonalizeScreen.vue';
import FoldingDirectionsScreen from './components/FoldingDirectionsScreen.vue';
import HowToUseScreen from './components/HowToUseScreen.vue';

export default {
  name: 'App',
  components: {
    SplashScreen, ChooseFoldScreen, PersonalizeScreen, FoldingDirectionsScreen, HowToUseScreen
  },
  data() {
    return {
      screen: 'SplashScreen',
      fold: null,
      front: true
    }
  },
  methods: {
  }
}
</script>

<style>
/* ── Figma Design Tokens ── */

/* Primitives */
:root {
  --aqua-bright:    #aefff1;
  --green-darkest:  #000807;
  --green-forest:   #0a4138;
  --green-lightest: #fdffff;
  --green-mat:      #267c69;
  --grey:           #7e8382;
}

/* Semantic */
:root {
  --shadow:          var(--grey);
  --surface-dark:    var(--green-mat);
  --surface-darker:  var(--green-forest);
  --surface-darkest: var(--green-darkest);
  --surface-light:   var(--green-lightest);
  --text-bright:     var(--aqua-bright);
  --text-dark:       var(--green-darkest);
  --text-lightest:   var(--green-lightest);

  /* Legacy alias kept for any components that still reference it */
  --bg-color: var(--surface-dark);
}

/* Typography */
:root {
  --font-line-height-18: 1.125rem;
  --font-line-height-20: 1.25rem;
  --font-line-height-24: 1.5rem;
  --font-size-12: 0.75rem;
  --font-size-14: 0.875rem;
  --font-size-16: 1rem;
  --font-size-24: 1.5rem;
  --font-weight-400: 400;
  --font-weight-700: 700;
  --font-family-balsamiq: 'Balsamiq Sans', cursive;
}

/* ── Global Base Styles ── */
body {
  margin: 0;
  background-color: var(--surface-dark);
  color: var(--text-bright);
  font-family: var(--font-family-balsamiq);
}

.lazy-font-loader {
  opacity: 0;
  height: 0;
}

/* ── Draftsman's Over-shoot Border ──────────────────────────────────────────
   Apply class="drafting-border" to any rectangular element that needs the
   hand-drawn corner over-shoot effect. Uses only ::before and ::after so
   it is safe on elements that don't have their own pseudo-elements.
   ─────────────────────────────────────────────────────────────────────── */
.drafting-border {
  position: relative;
  border: 1.5px solid var(--aqua-bright);
  /* Apply the SVG pencil-wobble filter to the whole element */
  filter: url(#sketchy-border);
}

/* Horizontal rules — extend 4px past left & right edges */
.drafting-border::before {
  content: "";
  position: absolute;
  left: -4px;
  right: -4px;
  height: 1.5px;
  background: var(--aqua-bright);
  top: -4px;
  box-shadow: 0 calc(100% + 8px) 0 0 var(--aqua-bright);
  pointer-events: none;
}

/* Vertical rules — extend 4px past top & bottom edges */
.drafting-border::after {
  content: "";
  position: absolute;
  top: -4px;
  bottom: -4px;
  width: 1.5px;
  background: var(--aqua-bright);
  left: -4px;
  box-shadow: calc(100% + 8px) 0 0 0 var(--aqua-bright);
  pointer-events: none;
}

@font-face {
  font-family: Cabin-Sketch;
  src: url("~@/assets/fonts/CabinSketch-Bold.ttf");
}

@font-face {
  font-family: Noto-Sans;
  src: url("~@/assets/fonts/NotoSans-Regular.ttf");
}

@font-face {
  font-family: AdventPro-Thin;
  src: url("~@/assets/fonts/AdventPro-Thin.ttf");
}

@font-face {
  font-family: Cookie-Regular;
  src: url("~@/assets/fonts/Cookie-Regular.ttf");
}

@font-face {
  font-family: CourierPrime-Regular;
  src: url("~@/assets/fonts/CourierPrime-Regular.ttf");
}

@font-face {
  font-family: CraftyGirls-Regular;
  src: url("~@/assets/fonts/CraftyGirls-Regular.ttf");
}

@font-face {
  font-family: DecovarAlpha-VF;
  src: url("~@/assets/fonts/DecovarAlpha-VF.ttf");
}

@font-face {
  font-family: Dekko-Regular;
  src: url("~@/assets/fonts/Dekko-Regular.ttf");
}

@font-face {
  font-family: DrSugiyama-Regular;
  src: url("~@/assets/fonts/DrSugiyama-Regular.ttf");
}

@font-face {
  font-family: EastSeaDokdo-Regular;
  src: url("~@/assets/fonts/EastSeaDokdo-Regular.ttf");
}

@font-face {
  font-family: EaterCaps-Regular;
  src: url("~@/assets/fonts/EaterCaps-Regular.ttf");
}

@font-face {
  font-family: FasterOne-Regular;
  src: url("~@/assets/fonts/FasterOne-Regular.ttf");
}

@font-face {
  font-family: Felipa-Regular;
  src: url("~@/assets/fonts/Felipa-Regular.ttf");
}

@font-face {
  font-family: FreckleFace-Regular;
  src: url("~@/assets/fonts/FreckleFace-Regular.ttf");
}

@font-face {
  font-family: FrederickatheGreat-Regular;
  src: url("~@/assets/fonts/FrederickatheGreat-Regular.ttf");
}

@font-face {
  font-family: Griffy-Regular;
  src: url("~@/assets/fonts/Griffy-Regular.ttf");
}

@font-face {
  font-family: IndieFlower-Regular;
  src: url("~@/assets/fonts/IndieFlower-Regular.ttf");
}

@font-face {
  font-family: Italianno-Regular;
  src: url("~@/assets/fonts/Italianno-Regular.ttf");
}

@font-face {
  font-family: JacquesFrancoisShadow-Regular;
  src: url("~@/assets/fonts/JacquesFrancoisShadow-Regular.ttf");
}

@font-face {
  font-family: Kodchasan-Regular;
  src: url("~@/assets/fonts/Kodchasan-Regular.ttf");
}

@font-face {
  font-family: KumarOne-Regular;
  src: url("~@/assets/fonts/KumarOne-Regular.ttf");
}

@font-face {
  font-family: LiuJianMaoCao-Regular;
  src: url("~@/assets/fonts/LiuJianMaoCao-Regular.ttf");
}

@font-face {
  font-family: LobsterTwo-Regular;
  src: url("~@/assets/fonts/LobsterTwo-Regular.ttf");
}

@font-face {
  font-family: LoveYaLikeASister;
  src: url("~@/assets/fonts/LoveYaLikeASister.ttf");
}

@font-face {
  font-family: LovedbytheKing;
  src: url("~@/assets/fonts/LovedbytheKing.ttf");
}

@font-face {
  font-family: MarckScript-Regular;
  src: url("~@/assets/fonts/MarckScript-Regular.ttf");
}

@font-face {
  font-family: MedievalSharp;
  src: url("~@/assets/fonts/MedievalSharp.ttf");
}

@font-face {
  font-family: Megrim;
  src: url("~@/assets/fonts/Megrim.ttf");
}

@font-face {
  font-family: Molle-Regular;
  src: url("~@/assets/fonts/Molle-Regular.ttf");
}

@font-face {
  font-family: NanumBrushScript-Regular;
  src: url("~@/assets/fonts/NanumBrushScript-Regular.ttf");
}
</style>
